import { consoleRoutes, webappAgentUrl } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, Segmented } from "@sagot/ui";
import { useState } from "react";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { formatDate } from "../shared/format";
import type { WidgetOut } from "../shared/types";
import { HomePreview } from "./home-preview";
import { HomeStats } from "./home-stats";
import type { WidgetForm, WidgetTab } from "./widget-model";
import { WidgetPreview, type PreviewDevice } from "./widget-preview";

const K = "deployKnowledge.widgetStudio";
const P = `${K}.preview`;

interface PreviewPaneProps {
  /** The open Studio tab: Home swaps the site mock-up for the Home / Messenger preview. */
  tab: WidgetTab;
  agentId: string;
  form: WidgetForm;
  agentName: string;
  defaultGreeting: string;
  siteName: string;
  domain: string | null;
  widget: WidgetOut;
  canEdit: boolean;
  dirty: boolean;
  saving: boolean;
  publishing: boolean;
  onSave: () => void;
  onPublish: () => void;
}

export function PreviewPane({ tab, agentId, form, agentName, defaultGreeting, siteName, domain, widget, canEdit, dirty, saving, publishing, onSave, onPublish }: PreviewPaneProps) {
  const { t, i18n } = useTranslation();
  const { summary } = useWorkspaceContext();
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const published = widget.published;
  const home = tab === "home";
  const optionLabels = new Map(form.home.options.map((option) => [option.id, option.label]));
  return (
    <section className="min-w-0 flex-1 bg-canvas p-4 lg:overflow-y-auto lg:p-8" aria-labelledby="widget-preview-title">
      <div className="mx-auto max-w-5xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <h2 id="widget-preview-title" className="flex items-center gap-2 font-semibold">
              <span aria-hidden="true" className="size-2 rounded-pill bg-success" />
              {t(`${P}.live`)} <span className="font-normal text-fg-subtle">{t(`${P}.liveHint`)}</span>
            </h2>
            <p className="mt-0.5 text-xs text-fg-subtle">
              {published ? t(`${K}.publishedVersion`, { version: published.version, date: formatDate(published.published_at, i18n.language, true) }) : t(`${K}.neverPublished`)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {dirty ? (
              <Badge tone="warning" data-testid="unsaved">
                <Icon name="alert-circle" className="size-3" />
                {t("deployKnowledge.unsavedChanges")}
              </Badge>
            ) : null}
            {home ? null : (
              <Segmented
                aria-label={t(`${P}.device`)}
                value={device}
                onValueChange={setDevice}
                options={[
                  { value: "desktop", label: t(`${P}.desktop`) },
                  { value: "mobile", label: t(`${P}.mobile`) },
                ]}
              />
            )}
            <Button asChild variant="secondary" size="sm">
              <a href={webappAgentUrl(summary.slug, widget.agent_key)} target="_blank" rel="noopener noreferrer" aria-label={t(`${P}.openDemoLabel`)}>
                <Icon name="external-link" />
                {t(`${P}.openDemo`)}
              </a>
            </Button>
            {canEdit ? (
              <>
                <Button variant="secondary" size="sm" disabled={!dirty} loading={saving} onClick={onSave}>
                  {t(`${K}.saveDraft`)}
                </Button>
                <Button size="sm" loading={publishing} onClick={onPublish}>
                  <Icon name="upload-cloud" />
                  {t(`${K}.publish`)}
                </Button>
              </>
            ) : null}
          </div>
        </div>
        {home ? (
          <div className="mx-auto max-w-xl space-y-6">
            <HomePreview form={form} agentName={agentName} pageName={siteName} />
            <HomeStats agentId={agentId} active labels={optionLabels} />
          </div>
        ) : (
          <>
            <WidgetPreview form={form} agentName={agentName} defaultGreeting={defaultGreeting} siteName={siteName} domain={domain} device={device} />
            <p className="mt-3 text-center text-xs text-fg-subtle">
              {t(`${P}.footer`)}{" "}
              <AppLink href={consoleRoutes.playground(summary.slug, agentId)} className="font-semibold text-primary-ink hover:underline">
                {t(`${P}.playground`)}
              </AppLink>
              .
            </p>
          </>
        )}
      </div>
    </section>
  );
}
